/**
 * Single source of truth for the collection "kind" enum.
 * Adding a new kind should mean editing this file and nothing else
 * (UI tiles, dashboard groupings, API validation, etc. all read from here).
 */

export const COLLECTION_KINDS = ["drop", "simple", "badge", "1of1"] as const;
export type CollectionKind = (typeof COLLECTION_KINDS)[number];

export type KindMetadata = {
  /** Short title for the wizard tile and tabs. */
  title: string;
  /** Long description shown in the wizard tile. */
  body: string;
  /** Solidity contract name (matches packages/abis exports). */
  contract: string;
  /** What happens at deploy time (shown in the wizard tile). */
  deploys: string;
  /** Plural label for the dashboard section header. */
  pluralLabel: string;
  /** Short pill text rendered on the home grid tiles. */
  pillLabel: string;
  /** Tailwind classes for the home grid pill background+text. */
  pillClasses: string;
  /** Whether `factory.create<Kind>` needs to be called when registering. */
  requiresFactoryDeploy: boolean;
  /** Whether this kind shares a single canonical contract per chain. */
  isShared: boolean;
};

export const KIND_METADATA: Record<CollectionKind, KindMetadata> = {
  drop: {
    title: "Drop",
    body: "Public mint. Anyone with the link can mint. You set price, max supply, time window, royalty and per-wallet cap. Pause, withdraw and rotate base URI any time.",
    contract: "ERC721Drop",
    deploys: "deploys a new ERC-721 clone you own",
    pluralLabel: "Drops",
    pillLabel: "drop",
    pillClasses: "bg-foreground text-background",
    requiresFactoryDeploy: true,
    isShared: false,
  },
  simple: {
    title: "Simple",
    body: "Owner-only mint. One-by-one or in batches to specific addresses with custom per-token URIs. Best for airdrops or hand-curated collections.",
    contract: "ERC721Simple",
    deploys: "deploys a new ERC-721 clone you own",
    pluralLabel: "Simple collections",
    pillLabel: "simple",
    pillClasses: "bg-muted text-foreground",
    requiresFactoryDeploy: true,
    isShared: false,
  },
  badge: {
    title: "Badge",
    body: "Distribute single-use claim links / QR codes. Anyone with a link can claim — perfect for event attendance, allowlists, or completion proofs. Optionally soulbound. You can subsidize gas so claimers don't pay.",
    contract: "ERC721Badge",
    deploys: "deploys a new ERC-721 clone you own",
    pluralLabel: "Badges",
    pillLabel: "badge",
    pillClasses: "bg-indigo-500 text-indigo-50",
    requiresFactoryDeploy: true,
    isShared: false,
  },
  "1of1": {
    title: "1\u202F/\u202F1",
    body: "Single piece, no contract. Mint a unique piece into the shared Atelier1of1 contract. Each token is independent and you keep ownership.",
    contract: "Atelier1of1",
    deploys: "uses the existing shared contract — no deploy",
    pluralLabel: "1/1 pieces",
    pillLabel: "1/1",
    pillClasses: "bg-amber-400 text-amber-950",
    requiresFactoryDeploy: false,
    isShared: true,
  },
};

/** Type guard for narrowing strings (e.g. URL params, API responses). */
export function isCollectionKind(v: unknown): v is CollectionKind {
  return typeof v === "string" && (COLLECTION_KINDS as readonly string[]).includes(v);
}
